import { forwardRef, useEffect, useRef, useState, Children, isValidElement } from "react";
import { Icon } from "@iconify/react";

// Accepts either an `options` prop ([{ value, label, disabled }]) or plain
// <option> children, so it drops in for native <select> usages unchanged.
const extractOptions = (children) => {
  if (!children) return [];
  return Children.toArray(children)
    .filter((child) => isValidElement(child))
    .map((child) => ({
      value: child.props.value ?? "",
      label: child.props.children,
      disabled: child.props.disabled,
    }));
};

const Select = forwardRef(
  (
    {
      label,
      error,
      helperText,
      options,
      children,
      value,
      onChange,
      disabled = false,
      placeholder = "Select...",
      className = "",
      wrapperClassName = "mb-4",
      name,
      id,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    const resolvedOptions = options || extractOptions(children);
    const selected = resolvedOptions.find((opt) => String(opt.value) === String(value ?? ""));

    useEffect(() => {
      const handler = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Synthesize a native-event-shaped payload so existing
    // `onChange={(e) => setX(e.target.value)}` call sites keep working.
    const emitChange = (nextValue) => {
      onChange?.({ target: { name, value: nextValue } });
    };

    return (
      <div className={wrapperClassName} ref={wrapperRef}>
        {label && <label className="form-label">{label}</label>}
        <div className="relative">
          <button
            ref={ref}
            type="button"
            id={id}
            disabled={disabled}
            onClick={() => !disabled && setOpen((prev) => !prev)}
            className={`form-control w-full flex items-center justify-between gap-2 text-left
              ${error ? "border-danger focus:border-danger focus:ring-danger/10" : ""}
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              ${open ? "ring-2 ring-primary/20 border-primary" : ""}
              ${className}`}
            {...props}
          >
            <span className={`truncate ${!selected ? "text-gray-400" : ""}`}>
              {selected ? selected.label : placeholder}
            </span>
            <Icon
              icon={open ? "mdi:chevron-up" : "mdi:chevron-down"}
              className="text-gray-400 text-lg shrink-0"
            />
          </button>

          {open && !disabled && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {resolvedOptions.map((opt) => {
                const isSelected = String(opt.value) === String(value ?? "");
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => {
                      emitChange(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors border-b border-gray-100 last:border-b-0 disabled:opacity-40 disabled:cursor-not-allowed
                      ${isSelected ? "bg-primary/10 text-primary font-medium" : "text-gray-700 hover:bg-primary/5"}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-danger">{error}</p>}
        {helperText && !error && (
          <p className="mt-1 text-sm text-text-secondary">{helperText}</p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
